import React, { ReactElement, ReactNode } from 'react';

import { ItemVariant } from '../constants';

export interface UseTooltipLabelProps {
    /** Label content. */
    label: string | React.ReactNode;
    /** Variant of the navigation item to display text and/or icon (default = both) */
    variant?: ItemVariant;
}

const getLabelNodeText = (node: ReactNode): string => {
    if (typeof node === 'string') {
        return node;
    }

    if (typeof node === 'number') {
        return node.toString();
    }

    if (typeof node === 'object' && node && !Array.isArray(node)) {
        return getLabelNodeText((node as ReactElement).props.children);
    }

    return '';
};

export const useTooltipLabel = ({ label, variant }: UseTooltipLabelProps) => {
    const labelRef = React.useRef<HTMLElement>(null);
    const [isTruncated, setTruncated] = React.useState(false);

    const labelText = React.useMemo(() => getLabelNodeText(label), [label]);
    const isIconOnly = variant === ItemVariant.iconOnly;

    /*
     * Measured again on every label or variant change. A visually hidden label has no width, which
     * is not the same as overflowing — hence the `offsetWidth > 0` guard. And a label that stops
     * overflowing has to lose its tooltip again, hence setting the state in both directions.
     */
    React.useLayoutEffect(() => {
        // An icon-only item gets its tooltip either way, so reading the width would force a
        // layout for a value nobody reads — and that happens exactly when the sidebar collapses.
        if (isIconOnly) {
            return;
        }

        const element = labelRef.current;

        setTruncated(!!element && element.offsetWidth > 0 && element.offsetWidth < element.scrollWidth);
    }, [labelText, isIconOnly]);

    // An icon-only item has no visible label, so the tooltip is the only way to read it.
    return { labelRef, tooltipLabel: isIconOnly || isTruncated ? labelText : '' };
};
